import { useState, useEffect, useCallback } from "react";
import { Announcement } from "@/pages/announcements/page";

const CATEGORY_LABELS: Record<string, string> = {
  haber: "Haber",
  duyuru: "Duyuru",
  blog: "Blog",
  kampanya: "Kampanya",
};

const CATEGORY_COLORS: Record<string, string> = {
  haber: "bg-sky-100 text-sky-700",
  duyuru: "bg-amber-100 text-amber-700",
  blog: "bg-emerald-100 text-emerald-700",
  kampanya: "bg-rose-100 text-rose-700",
};

const EMPTY_FORM: Omit<Announcement, "id" | "publishedAt"> = {
  title: "",
  content: "",
  category: "duyuru",
  author: "Editör Ekibi",
  image: "",
  pinned: false,
  tags: [],
};

// Preset image suggestions per category
const IMAGE_PRESETS: Record<string, { label: string; prompt: string; seq: string }[]> = {
  duyuru: [
    { label: "Platform Duyurusu", prompt: "Turkey travel platform announcement digital modern clean interface warm amber tones editorial", seq: "preset-ann-1" },
    { label: "Yeni Özellik", prompt: "new feature launch digital product announcement celebration confetti warm tones modern", seq: "preset-ann-2" },
    { label: "Güncelleme", prompt: "software update announcement technology modern interface clean minimal warm light", seq: "preset-ann-3" },
  ],
  haber: [
    { label: "Genel Haber", prompt: "Turkey travel news editorial magazine layout warm amber tones beautiful landscape", seq: "preset-hab-1" },
    { label: "Seyahat Haberi", prompt: "Turkey tourism travel news beautiful destinations landscape editorial photography warm", seq: "preset-hab-2" },
    { label: "İşletme Haberi", prompt: "Turkish business restaurant hotel announcement news editorial warm tones professional", seq: "preset-hab-3" },
  ],
  blog: [
    { label: "Seyahat Blog", prompt: "Turkey travel blog scenic landscape mountains sea editorial photography warm golden light", seq: "preset-blg-1" },
    { label: "Yemek Blog", prompt: "Turkish cuisine food blog colorful dishes meze kebab baklava editorial photography", seq: "preset-blg-2" },
    { label: "Doğa Blog", prompt: "Turkey nature landscape green mountains forest yayla plateau misty fog scenic editorial", seq: "preset-blg-3" },
    { label: "Şehir Blog", prompt: "Turkey city urban architecture historical streets editorial photography warm tones", seq: "preset-blg-4" },
  ],
  kampanya: [
    { label: "Yaz Kampanyası", prompt: "Turkey summer campaign beach sea turquoise warm sunny holiday promotion editorial", seq: "preset-kmp-1" },
    { label: "Kış Kampanyası", prompt: "Turkey winter campaign snow mountains Cappadocia cold season holiday promotion editorial", seq: "preset-kmp-2" },
    { label: "İndirim Kampanyası", prompt: "discount sale promotion campaign warm amber tones celebration special offer editorial", seq: "preset-kmp-3" },
  ],
};

interface ImagePickerProps {
  category: Announcement["category"];
  currentImage: string;
  onSelect: (url: string) => void;
}

const ImagePicker = ({ category, currentImage, onSelect }: ImagePickerProps) => {
  const [mode, setMode] = useState<"preset" | "url" | "custom">("preset");
  const [urlInput, setUrlInput] = useState(currentImage || "");
  const [customPrompt, setCustomPrompt] = useState("");
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const presets = IMAGE_PRESETS[category] || IMAGE_PRESETS.duyuru;

  const buildUrl = useCallback((prompt: string, seq: string) =>
    `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28prompt%29%7D&width=800&height=400&seq=${seq}&orientation=landscape`,
  []);

  const handlePresetSelect = (preset: { prompt: string; seq: string }) => {
    const url = buildUrl(preset.prompt, preset.seq);
    onSelect(url);
    setPreviewError(false);
  };

  const handleUrlApply = () => {
    if (urlInput.trim()) {
      onSelect(urlInput.trim());
      setPreviewError(false);
    }
  };

  const handleGenerate = () => {
    if (!customPrompt.trim()) return;
    setGenerating(true);
    const seq = "custom-" + Date.now();
    const url = buildUrl(customPrompt, seq);
    setGeneratedUrl(url);
    setTimeout(() => {
      onSelect(url);
      setGenerating(false);
    }, 800);
  };

  return (
    <div className="space-y-3">
      <label className="block text-xs font-semibold text-stone-600">Görsel Seç</label>

      {/* Mode tabs */}
      <div className="flex items-center bg-stone-100 rounded-xl p-1 gap-1">
        {([
          { key: "preset", label: "Hazır Görseller", icon: "ri-image-2-line" },
          { key: "custom", label: "AI Oluştur", icon: "ri-magic-line" },
          { key: "url", label: "URL Gir", icon: "ri-link-m" },
        ] as const).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setMode(tab.key)}
            className={`flex items-center gap-1.5 flex-1 justify-center text-xs px-2 py-2 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
              mode === tab.key ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"
            }`}
          >
            <i className={tab.icon}></i>
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Preset grid */}
      {mode === "preset" && (
        <div className="grid grid-cols-3 gap-2">
          {presets.map((preset) => {
            const url = buildUrl(preset.prompt, preset.seq);
            const isSelected = currentImage === url;
            return (
              <button
                key={preset.seq}
                type="button"
                onClick={() => handlePresetSelect(preset)}
                className={`relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer group ${
                  isSelected ? "border-amber-500" : "border-transparent hover:border-amber-300"
                }`}
              >
                <div className="h-20 bg-stone-100">
                  <img
                    src={url}
                    alt={preset.label}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div className={`absolute inset-0 flex items-end p-1.5 transition-all ${
                  isSelected ? "bg-amber-500/20" : "bg-black/0 group-hover:bg-black/20"
                }`}>
                  {isSelected && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center bg-amber-500 rounded-full">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  )}
                  <span className="text-white text-xs font-semibold drop-shadow-sm line-clamp-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {preset.label}
                  </span>
                </div>
              </button>
            );
          })}
          {/* No image option */}
          <button
            type="button"
            onClick={() => onSelect("")}
            className={`relative rounded-xl border-2 transition-all cursor-pointer h-20 flex flex-col items-center justify-center gap-1 ${
              !currentImage ? "border-stone-400 bg-stone-100" : "border-stone-200 bg-stone-50 hover:border-stone-300"
            }`}
          >
            <i className="ri-image-line text-stone-400 text-lg"></i>
            <span className="text-xs text-stone-400">Görselsiz</span>
          </button>
        </div>
      )}

      {/* Custom AI prompt */}
      {mode === "custom" && (
        <div className="space-y-3">
          <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
            <p className="text-xs font-semibold text-amber-800 mb-1">
              <i className="ri-magic-line mr-1"></i>AI Görsel Oluştur
            </p>
            <p className="text-xs text-amber-700">İngilizce bir açıklama yazın, AI sizin için görsel oluşturur.</p>
          </div>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            rows={3}
            placeholder="Örn: Beautiful Turkish landscape with mountains and traditional village, warm golden sunset, editorial photography style..."
            className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
          />
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!customPrompt.trim() || generating}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            {generating ? (
              <><i className="ri-loader-4-line animate-spin"></i> Oluşturuluyor...</>
            ) : (
              <><i className="ri-magic-line"></i> Görsel Oluştur</>
            )}
          </button>
          {generatedUrl && !generating && (
            <div className="rounded-xl overflow-hidden border border-amber-200 h-32">
              <img src={generatedUrl} alt="Oluşturulan görsel" className="w-full h-full object-cover object-top" />
            </div>
          )}
        </div>
      )}

      {/* URL input */}
      {mode === "url" && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => { setUrlInput(e.target.value); setPreviewError(false); }}
              placeholder="https://example.com/image.jpg"
              className="flex-1 border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
            />
            <button
              type="button"
              onClick={handleUrlApply}
              className="px-4 py-2.5 bg-stone-900 text-white text-sm font-semibold rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Uygula
            </button>
          </div>
          {urlInput && !previewError && (
            <div className="h-28 rounded-xl overflow-hidden border border-stone-200 bg-stone-100">
              <img
                src={urlInput}
                alt="Önizleme"
                className="w-full h-full object-cover object-top"
                onError={() => setPreviewError(true)}
              />
            </div>
          )}
          {previewError && (
            <div className="h-16 rounded-xl border border-rose-200 bg-rose-50 flex items-center justify-center gap-2">
              <i className="ri-error-warning-line text-rose-400"></i>
              <span className="text-xs text-rose-500">Görsel yüklenemedi, URL'yi kontrol edin</span>
            </div>
          )}
        </div>
      )}

      {/* Current image preview (always shown if set) */}
      {currentImage && mode === "preset" && (
        <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
            <img src={currentImage} alt="Seçili" className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-emerald-700">Görsel seçildi</p>
            <p className="text-xs text-emerald-600 truncate">{currentImage.slice(0, 50)}...</p>
          </div>
          <button
            type="button"
            onClick={() => onSelect("")}
            className="w-6 h-6 flex items-center justify-center text-emerald-500 hover:text-rose-500 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}
    </div>
  );
};

interface Props {
  showToast: (msg: string, type: "success" | "error" | "info") => void;
}

const AnnouncementsManager = ({ showToast }: Props) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [selected, setSelected] = useState<Announcement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<Omit<Announcement, "id" | "publishedAt">>(EMPTY_FORM);
  const [tagInput, setTagInput] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const load = () => {
    const stored: Announcement[] = JSON.parse(localStorage.getItem("siteAnnouncements") || "[]");
    setAnnouncements(stored.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()));
  };

  useEffect(() => { load(); }, []);

  const save = (list: Announcement[]) => {
    localStorage.setItem("siteAnnouncements", JSON.stringify(list));
    setAnnouncements(list.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()));
  };

  const handleCreate = () => {
    setForm(EMPTY_FORM);
    setTagInput("");
    setIsCreating(true);
    setIsEditing(false);
    setSelected(null);
  };

  const handleEdit = (ann: Announcement) => {
    setForm({
      title: ann.title,
      content: ann.content,
      category: ann.category,
      author: ann.author,
      image: ann.image || "",
      pinned: ann.pinned || false,
      tags: ann.tags || [],
    });
    setTagInput("");
    setIsEditing(true);
    setIsCreating(false);
    setSelected(ann);
  };

  const handleSave = () => {
    if (!form.title.trim() || !form.content.trim()) {
      showToast("Başlık ve içerik zorunludur.", "error");
      return;
    }
    const stored: Announcement[] = JSON.parse(localStorage.getItem("siteAnnouncements") || "[]");
    if (isCreating) {
      const newAnn: Announcement = {
        ...form,
        id: "ann-" + Date.now(),
        publishedAt: new Date().toISOString(),
      };
      const updated = [newAnn, ...stored];
      save(updated);
      setSelected(newAnn);
      showToast("Duyuru başarıyla yayınlandı!", "success");
    } else if (isEditing && selected) {
      const updated = stored.map((a) =>
        a.id === selected.id ? { ...a, ...form } : a
      );
      save(updated);
      setSelected({ ...selected, ...form });
      showToast("Duyuru güncellendi!", "success");
    }
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleDelete = (id: string) => {
    const stored: Announcement[] = JSON.parse(localStorage.getItem("siteAnnouncements") || "[]");
    const updated = stored.filter((a) => a.id !== id);
    save(updated);
    if (selected?.id === id) setSelected(null);
    setDeleteConfirm(null);
    showToast("Duyuru silindi.", "error");
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase().replace(/\s+/g, "-");
    if (tag && !(form.tags || []).includes(tag)) {
      setForm((f) => ({ ...f, tags: [...(f.tags || []), tag] }));
    }
    setTagInput("");
  };

  const handleRemoveTag = (tag: string) => {
    setForm((f) => ({ ...f, tags: (f.tags || []).filter((t) => t !== tag) }));
  };

  const handleImageSelect = useCallback((url: string) => {
    setForm((f) => ({ ...f, image: url }));
  }, []);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });

  const isFormOpen = isCreating || isEditing;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Duyuru & Blog Yönetimi
          </h2>
          <p className="text-xs text-stone-400 mt-0.5">Ziyaretçilere gösterilecek haberler, duyurular ve blog yazıları</p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/duyurular"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs px-3 py-2 bg-stone-100 text-stone-600 rounded-xl hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line"></i> Sayfayı Gör
          </a>
          <button
            onClick={handleCreate}
            className="flex items-center gap-1.5 text-xs px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap font-semibold"
          >
            <i className="ri-add-line"></i> Yeni Duyuru
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: List */}
        <div className="lg:col-span-1 space-y-2">
          {announcements.length === 0 && !isFormOpen && (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <i className="ri-megaphone-line text-stone-300 text-3xl"></i>
              </div>
              <p className="text-stone-400 text-sm">Henüz duyuru yok.</p>
              <p className="text-stone-300 text-xs mt-1">Yeni duyuru oluşturmak için butona tıklayın.</p>
            </div>
          )}
          {announcements.map((ann) => (
            <div
              key={ann.id}
              className={`relative p-3 rounded-xl border transition-all cursor-pointer ${
                selected?.id === ann.id && !isFormOpen
                  ? "border-amber-400 bg-amber-50"
                  : "border-stone-100 bg-white hover:border-amber-200"
              }`}
              onClick={() => { setSelected(ann); setIsCreating(false); setIsEditing(false); }}
            >
              <div className="flex gap-3">
                {ann.image && (
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={ann.image} alt={ann.title} className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <div className="flex items-center gap-1 flex-wrap">
                      {ann.pinned && <i className="ri-pushpin-fill text-amber-500 text-xs"></i>}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[ann.category]}`}>
                        {CATEGORY_LABELS[ann.category]}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(ann); }}
                        className="w-6 h-6 flex items-center justify-center text-stone-400 hover:text-amber-600 cursor-pointer transition-colors"
                      >
                        <i className="ri-edit-line text-xs"></i>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm(ann.id); }}
                        className="w-6 h-6 flex items-center justify-center text-stone-400 hover:text-rose-500 cursor-pointer transition-colors"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                  </div>
                  <h3 className="font-semibold text-stone-900 text-xs leading-snug mb-0.5 line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {ann.title}
                  </h3>
                  <p className="text-xs text-stone-400">{formatDate(ann.publishedAt)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Right: Form or Detail */}
        <div className="lg:col-span-2">
          {/* Delete Confirm Modal */}
          {deleteConfirm && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-rose-100 rounded-full">
                  <i className="ri-delete-bin-line text-rose-500 text-xl"></i>
                </div>
                <h3 className="text-base font-bold text-stone-900 text-center mb-2">Duyuruyu Sil</h3>
                <p className="text-stone-500 text-sm text-center mb-6">Bu duyuru kalıcı olarak silinecek. Emin misiniz?</p>
                <div className="flex gap-3">
                  <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-stone-200 text-stone-600 text-sm font-medium rounded-xl hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
                  <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-rose-500 text-white text-sm font-medium rounded-xl hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap">Sil</button>
                </div>
              </div>
            </div>
          )}

          {/* Create / Edit Form */}
          {isFormOpen ? (
            <div className="bg-white rounded-2xl border border-stone-100 p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-stone-900 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {isCreating ? "Yeni Duyuru Oluştur" : "Duyuruyu Düzenle"}
                </h3>
                <button
                  onClick={() => { setIsCreating(false); setIsEditing(false); }}
                  className="w-8 h-8 flex items-center justify-center hover:bg-stone-100 rounded-full cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-stone-500"></i>
                </button>
              </div>

              <div className="space-y-5">
                {/* Title */}
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Başlık *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="Duyuru başlığı..."
                    className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>

                {/* Category + Author */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Kategori</label>
                    <select
                      value={form.category}
                      onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as Announcement["category"] }))}
                      className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 bg-white"
                    >
                      <option value="duyuru">Duyuru</option>
                      <option value="haber">Haber</option>
                      <option value="blog">Blog</option>
                      <option value="kampanya">Kampanya</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Yazar</label>
                    <input
                      type="text"
                      value={form.author}
                      onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                      placeholder="Yazar adı"
                      className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Content */}
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">İçerik *</label>
                  <textarea
                    value={form.content}
                    onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                    rows={5}
                    maxLength={2000}
                    placeholder="Duyuru içeriğini buraya yazın..."
                    className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
                  />
                  <p className="text-xs text-stone-400 text-right mt-1">{form.content.length}/2000</p>
                </div>

                {/* Image Picker */}
                <div className="border border-stone-100 rounded-2xl p-4 bg-stone-50">
                  <ImagePicker
                    category={form.category}
                    currentImage={form.image || ""}
                    onSelect={handleImageSelect}
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Etiketler</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddTag(); } }}
                      placeholder="Etiket ekle ve Enter'a bas"
                      className="flex-1 border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-amber-400 transition-colors"
                    />
                    <button type="button" onClick={handleAddTag} className="px-3 py-2 bg-stone-100 text-stone-600 rounded-xl text-sm hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap">
                      Ekle
                    </button>
                  </div>
                  {(form.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {(form.tags || []).map((tag) => (
                        <span key={tag} className="flex items-center gap-1 bg-stone-100 text-stone-600 text-xs px-2.5 py-1 rounded-full">
                          #{tag}
                          <button onClick={() => handleRemoveTag(tag)} className="w-3 h-3 flex items-center justify-center hover:text-rose-500 cursor-pointer">
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pinned toggle */}
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, pinned: !f.pinned }))}
                    className={`w-10 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 relative ${form.pinned ? "bg-amber-500" : "bg-stone-200"}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${form.pinned ? "left-5" : "left-1"}`}></div>
                  </button>
                  <div>
                    <p className="text-xs font-semibold text-stone-700">Sayfanın Başına Sabitle</p>
                    <p className="text-xs text-stone-400">Sabitlenmiş duyurular her zaman en üstte görünür</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => { setIsCreating(false); setIsEditing(false); }}
                    className="flex-1 py-2.5 border border-stone-200 text-stone-600 text-sm font-medium rounded-xl hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-amber-500 text-white text-sm font-semibold rounded-xl hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line"></i>
                    {isCreating ? "Yayınla" : "Güncelle"}
                  </button>
                </div>
              </div>
            </div>
          ) : selected ? (
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              {selected.image && (
                <div className="h-52 overflow-hidden">
                  <img src={selected.image} alt={selected.title} className="w-full h-full object-cover object-top" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {selected.pinned && (
                    <span className="flex items-center gap-1 bg-amber-100 text-amber-700 text-xs px-2.5 py-1 rounded-full font-semibold">
                      <i className="ri-pushpin-fill text-xs"></i> Sabit
                    </span>
                  )}
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${CATEGORY_COLORS[selected.category]}`}>
                    {CATEGORY_LABELS[selected.category]}
                  </span>
                  <span className="text-xs text-stone-400">{formatDate(selected.publishedAt)} · {selected.author}</span>
                </div>
                <h2 className="text-xl font-bold text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {selected.title}
                </h2>
                <p className="text-stone-600 text-sm leading-relaxed mb-4">{selected.content}</p>
                {selected.tags && selected.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {selected.tags.map((tag) => (
                      <span key={tag} className="text-xs bg-stone-100 text-stone-500 px-2.5 py-1 rounded-full">#{tag}</span>
                    ))}
                  </div>
                )}
                <div className="flex gap-3 pt-4 border-t border-stone-100">
                  <button
                    onClick={() => handleEdit(selected)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-stone-900 text-white text-sm font-semibold rounded-xl hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-edit-line"></i> Düzenle
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(selected.id)}
                    className="flex items-center gap-2 px-4 py-2.5 border border-rose-200 text-rose-600 text-sm font-semibold rounded-xl hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line"></i> Sil
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-stone-100 h-64 flex flex-col items-center justify-center">
              <div className="w-12 h-12 flex items-center justify-center mb-3">
                <i className="ri-megaphone-line text-stone-300 text-3xl"></i>
              </div>
              <p className="text-stone-400 text-sm">Detayları görmek için bir duyuru seçin</p>
              <button
                onClick={handleCreate}
                className="mt-4 flex items-center gap-1.5 text-xs px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap font-semibold"
              >
                <i className="ri-add-line"></i> İlk Duyuruyu Oluştur
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementsManager;
